/**
 * useDemandGenCache Hook
 *
 * Fetches all Demand Gen ad data for a campaign on page load.
 * This data is shared between CampaignElementsList and AssetManagement components
 * to ensure consistent display of Demand Gen ad content.
 *
 * Uses React Query for caching - results persist when navigating away and back.
 * Cache is invalidated when Demand Gen ads are added/removed via queryClient.invalidateQueries.
 *
 * Follows the same pattern as useRDACache.ts for consistency.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { getDemandGenAd, getLinkedImagesByEntity, type DemandGenAdType } from '@/services/api/assets';
import type { Campaign } from '@/types/campaign';

// Image asset type for cache (same structure as RDA)
export interface DemandGenCacheImageAsset {
  asset_id: string;
  url?: string;
  width?: number;
  height?: number;
}

// Carousel card type
export interface DemandGenCacheCarouselCard {
  headline: string;
  image_id?: string;  // Media Library image ID (for uploads)
  asset_id?: string;  // Google Ads asset ID (for existing cards)
  final_url?: string;
}

// Cache entry for a single Demand Gen ad
export interface DemandGenCacheEntry {
  ad_name?: string;
  ad_type?: DemandGenAdType;
  headlines: string[];
  descriptions: string[];
  business_name?: string;
  call_to_action?: string;
  final_urls?: string[];
  // Video ad fields
  long_headlines?: string[];
  video_ids?: string[];
  // Image assets (with thumbnail URLs merged in)
  marketing_images?: DemandGenCacheImageAsset[];
  square_marketing_images?: DemandGenCacheImageAsset[];
  portrait_marketing_images?: DemandGenCacheImageAsset[];
  logo_images?: DemandGenCacheImageAsset[];
  // Carousel ad fields
  carousel_cards?: DemandGenCacheCarouselCard[];
  // Thumbnail map for DemandGenAdEditor to use (google_ads_asset_id -> thumbnail_url)
  thumbnailMap?: Record<string, string>;
}

export interface UseDemandGenCacheResult {
  demandGenCache: Record<string, DemandGenCacheEntry>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Extract all Demand Gen ad IDs from a campaign's ad groups (excludes REMOVED ads)
 */
function extractDemandGenAdIds(campaign: Campaign): Array<{ adGroupId: string; adId: string }> {
  const dgIds: Array<{ adGroupId: string; adId: string }> = [];

  const adGroups = campaign.ads_group_list || [];
  for (const adGroup of adGroups) {
    const adGroupId = adGroup.ad_group_id || adGroup.ads_group_id || '';
    const ads = adGroup.ads_list || adGroup.ads || [];

    for (const ad of ads) {
      // Check if it's a Demand Gen ad type
      const adType = ad.ad_type || '';
      const isDemandGen = adType.includes('DEMAND_GEN') ||
                          adType === 'DEMAND_GEN_MULTI_ASSET_AD' ||
                          adType === 'DEMAND_GEN_VIDEO_RESPONSIVE_AD' ||
                          adType === 'DEMAND_GEN_CAROUSEL_AD';

      if (isDemandGen && ad.status !== 'REMOVED') {
        dgIds.push({ adGroupId, adId: ad.ad_id });
      }
    }
  }

  return dgIds;
}

/**
 * Fetch all Demand Gen ad data for a campaign, including thumbnail URLs from linked images
 */
async function fetchAllDemandGenAds(
  memberUuid: string,
  customerId: string,
  dgIds: Array<{ adGroupId: string; adId: string }>
): Promise<Record<string, DemandGenCacheEntry>> {
  const cache: Record<string, DemandGenCacheEntry> = {};

  // Fetch all DG ads in parallel (both DG ad data and linked images for thumbnails)
  const fetchPromises = dgIds.map(async ({ adGroupId, adId }) => {
    try {
      // Fetch DG ad data and linked images in parallel
      const [dgResponse, linkedImagesResponse] = await Promise.all([
        getDemandGenAd({
          member_uuid: memberUuid,
          ad_group_id: adGroupId,
          ad_id: adId,
          customer_id: customerId,
        }),
        getLinkedImagesByEntity({
          member_uuid: memberUuid,
          entity_type: 'ad',
          entity_id: adId,
          customer_id: customerId,
        }),
      ]);

      if (dgResponse.success) {
        // Build thumbnail map from linked images
        const thumbnailMap: Record<string, string> = {};
        if (linkedImagesResponse.success && linkedImagesResponse.images) {
          for (const img of linkedImagesResponse.images) {
            if (img.google_ads_asset_id && img.thumbnail_url) {
              thumbnailMap[img.google_ads_asset_id] = img.thumbnail_url;
            }
          }
        }

        // Helper to merge thumbnail URLs into image assets
        const mergeUrls = (assets: DemandGenCacheImageAsset[] | undefined): DemandGenCacheImageAsset[] => {
          if (!assets) return [];
          return assets.map((asset) => ({
            ...asset,
            url: thumbnailMap[asset.asset_id] || asset.url,
          }));
        };

        return {
          adId,
          data: {
            ad_name: dgResponse.ad_name,
            ad_type: dgResponse.ad_type,
            headlines: dgResponse.headlines || [],
            descriptions: dgResponse.descriptions || [],
            business_name: dgResponse.business_name,
            call_to_action: dgResponse.call_to_action,
            final_urls: dgResponse.final_urls,
            // Video fields
            long_headlines: dgResponse.long_headlines,
            video_ids: dgResponse.video_ids,
            // Include image assets with thumbnail URLs merged in
            marketing_images: mergeUrls(dgResponse.marketing_images),
            square_marketing_images: mergeUrls(dgResponse.square_marketing_images),
            portrait_marketing_images: mergeUrls(dgResponse.portrait_marketing_images),
            logo_images: mergeUrls(dgResponse.logo_images),
            // Carousel fields
            carousel_cards: dgResponse.carousel_cards,
            // Also store the thumbnail map for DemandGenAdEditor
            thumbnailMap,
          },
        };
      }
    } catch (err) {
      // Log but don't fail the entire batch for one DG ad
      console.warn(`Failed to fetch Demand Gen ad ${adId}:`, err);
    }
    return null;
  });

  const results = await Promise.all(fetchPromises);

  for (const result of results) {
    if (result) {
      cache[result.adId] = result.data;
    }
  }

  return cache;
}

/**
 * Hook to fetch and cache all Demand Gen ad data for a campaign.
 * Uses React Query for persistent caching across navigation.
 */
export function useDemandGenCache(campaign: Campaign | null | undefined): UseDemandGenCacheResult {
  const { memberUuid } = useAuth();

  // Extract all Demand Gen ad IDs from the campaign
  const dgIds = useMemo(() => {
    if (!campaign) return [];
    return extractDemandGenAdIds(campaign);
  }, [campaign]);

  // Create a stable key based on DG ad IDs for cache invalidation
  const dgIdsKey = useMemo(() => {
    return dgIds.map(r => r.adId).sort().join(',');
  }, [dgIds]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['demand-gen-cache', campaign?.campaign_id, dgIdsKey],
    queryFn: () => fetchAllDemandGenAds(
      memberUuid!,
      campaign!.google_ads_account_id!,
      dgIds
    ),
    enabled: !!memberUuid && !!campaign?.google_ads_account_id && dgIds.length > 0,
    staleTime: Infinity, // Don't refetch automatically - only on invalidation
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
  });

  return {
    demandGenCache: data ?? {},
    isLoading,
    error: error as Error | null,
  };
}

export default useDemandGenCache;
